import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Synthesizes the orb's sounds with the Web Audio API so the game ships no
 * audio assets: a soothing ambient pad under everything, a tap tone that
 * rises with the charge level, and a shimmering chord on the reveal.
 */

// Slow chord cycle for the background pad (Hz). Warm, unhurried, no leading tone.
const PAD_CHORDS = [
  [110.0, 164.81, 261.63], // A2 · E3 · C4
  [98.0, 146.83, 246.94],  // G2 · D3 · B3
  [130.81, 196.0, 293.66], // C3 · G3 · D4
  [87.31, 130.81, 220.0],  // F2 · C3 · A3
];
const CHORD_SECONDS = 11;
const PAD_VOLUME = 0.085;

// Drop a file in /public and point REACT_APP_BG_MUSIC at it to play a real
// track instead of the synthesized pad (e.g. REACT_APP_BG_MUSIC=/bg-music.mp3).
const MUSIC_URL = process.env.REACT_APP_BG_MUSIC || '';
const MUSIC_VOLUME = 0.35;

// A dev hot-reload of this file re-evaluates the module but doesn't unmount
// the component using it, so a plain module-level (or ref-held) AudioContext
// from the previous version keeps playing, orphaned — the new mute button
// then controls a context it never built. Pinning the real context to
// `window` means every reload of this file finds and reuses the same one,
// so there is only ever one context to mute, in dev or production.
function getSharedCtx() {
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtx) return null;
  if (!window.__orbAudioCtx || window.__orbAudioCtx.state === 'closed') {
    window.__orbAudioCtx = new AudioCtx();
  }
  return window.__orbAudioCtx;
}

// Every sound this hook makes goes through one master gain, so muting is an
// instant, synchronous silence rather than waiting on the context to suspend.
function getMaster(ctx) {
  if (!window.__orbMaster || window.__orbMaster.context !== ctx) {
    window.__orbMaster = ctx.createGain();
    window.__orbMaster.connect(ctx.destination);
  }
  return window.__orbMaster;
}

export function useOrbSound() {
  const padRef = useRef(null);
  const audioRef = useRef(null);
  const chordTimer = useRef(null);
  const [muted, setMuted] = useState(false);
  const mutedRef = useRef(muted);
  mutedRef.current = muted;

  const getCtx = useCallback(() => {
    if (mutedRef.current) return null;
    const ctx = getSharedCtx();
    if (!ctx) return null;
    // Browsers start the context suspended until a user gesture.
    if (ctx.state !== 'running') ctx.resume?.().catch?.(() => {});
    return ctx;
  }, []);

  const tone = useCallback((ctx, { freq, start, duration, peak, type = 'sine' }) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, start);
    gain.gain.setValueAtTime(0, start);
    gain.gain.linearRampToValueAtTime(peak, start + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    osc.connect(gain).connect(getMaster(ctx));
    osc.start(start);
    osc.stop(start + duration);
  }, []);

  /**
   * Builds the pad once: three voices (each lightly detuned against itself)
   * through a soft filter, breathing via a slow tremolo. Chords then cross-fade
   * on a timer, so nothing ever restarts or clicks.
   */
  const buildPad = useCallback((ctx) => {
    if (padRef.current) return;

    const master = ctx.createGain();
    master.gain.setValueAtTime(0, ctx.currentTime);
    master.gain.linearRampToValueAtTime(PAD_VOLUME, ctx.currentTime + 4);

    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(760, ctx.currentTime);
    filter.Q.value = 0.4;
    filter.connect(master).connect(getMaster(ctx));

    // Slow swell so the pad never sits still.
    const breath = ctx.createOscillator();
    const breathDepth = ctx.createGain();
    breath.frequency.value = 0.06;
    breathDepth.gain.value = PAD_VOLUME * 0.45;
    breath.connect(breathDepth).connect(master.gain);
    breath.start();

    const voices = PAD_CHORDS[0].map((freq, i) => {
      const gain = ctx.createGain();
      gain.gain.value = i === 0 ? 0.5 : 0.32;
      gain.connect(filter);
      return [0, 1].map((d) => {
        const osc = ctx.createOscillator();
        osc.type = i === 0 ? 'sine' : 'triangle';
        osc.frequency.setValueAtTime(freq, ctx.currentTime);
        osc.detune.value = d === 0 ? -5 : 5;
        osc.connect(gain);
        osc.start();
        return osc;
      });
    });

    let chordIndex = 0;
    chordTimer.current = setInterval(() => {
      chordIndex = (chordIndex + 1) % PAD_CHORDS.length;
      const at = ctx.currentTime;
      PAD_CHORDS[chordIndex].forEach((freq, i) => {
        voices[i].forEach((osc) => {
          osc.frequency.cancelScheduledValues(at);
          osc.frequency.setValueAtTime(osc.frequency.value, at);
          osc.frequency.linearRampToValueAtTime(freq, at + 3);
        });
      });
    }, CHORD_SECONDS * 1000);

    padRef.current = { master, oscillators: [...voices.flat(), breath] };
  }, []);

  /**
   * Starts the pad, waiting out the context's resume: `resume()` settles a
   * tick after the gesture, and a pad built on a still-suspended context
   * plays to nobody.
   */
  const startPad = useCallback(() => {
    if (padRef.current) return;
    const ctx = getCtx();
    if (!ctx) return;
    if (ctx.state === 'running') {
      buildPad(ctx);
      return;
    }
    Promise.resolve(ctx.resume?.())
      .then(() => {
        // A mute that landed while resume() was in flight must win.
        if (mutedRef.current) { ctx.suspend?.().catch?.(() => {}); return; }
        if (ctx.state === 'running') buildPad(ctx);
      })
      .catch(() => { /* still locked — the next gesture tries again */ });
  }, [getCtx, buildPad]);

  const stopPad = useCallback(() => {
    clearInterval(chordTimer.current);
    chordTimer.current = null;
    const pad = padRef.current;
    padRef.current = null;
    if (!pad || !window.__orbAudioCtx) return;
    const ctx = window.__orbAudioCtx;
    const at = ctx.currentTime;
    pad.master.gain.cancelScheduledValues(at);
    pad.master.gain.setValueAtTime(pad.master.gain.value, at);
    pad.master.gain.linearRampToValueAtTime(0, at + 0.6);
    pad.oscillators.forEach((osc) => {
      try { osc.stop(at + 0.7); } catch { /* already stopped */ }
    });
  }, []);

  /** The <audio> track, when one is configured in place of the synth pad. */
  const startTrack = useCallback(() => {
    if (!MUSIC_URL) return false;
    if (!audioRef.current) {
      const el = new Audio(MUSIC_URL);
      el.loop = true;
      el.volume = MUSIC_VOLUME;
      audioRef.current = el;
    }
    audioRef.current.play().catch(() => { /* still waiting for a gesture */ });
    return true;
  }, []);

  const stopTrack = useCallback(() => {
    audioRef.current?.pause();
  }, []);

  // Autoplay is blocked until the player interacts, and a context created
  // before that gesture starts out suspended — so every gesture resumes it
  // and (re)starts the music until it is genuinely running.
  useEffect(() => {
    if (muted) {
      // Mute tears the audio engine down completely: the pad's oscillators are
      // stopped and the context is closed, which kills every sound at the
      // source — including any orphaned by a hot reload. Unmuting builds a
      // fresh context and pad (getSharedCtx recreates a missing one).
      stopTrack();
      stopPad();
      const live = window.__orbAudioCtx;
      window.__orbAudioCtx = null;
      window.__orbMaster = null;
      live?.close?.().catch?.(() => {});
      return undefined;
    }

    const start = () => {
      if (startTrack()) return;
      // Unmuting has to wake the context first: the pad usually still exists
      // from before the mute, so startPad() alone would return early and
      // leave the context suspended — i.e. unmute doing nothing.
      const ctx = getCtx();
      if (ctx) {
        const gain = getMaster(ctx).gain;
        gain.cancelScheduledValues(ctx.currentTime);
        gain.setValueAtTime(1, ctx.currentTime);
      }
      startPad();
    };

    const events = ['pointerdown', 'keydown', 'touchstart'];
    events.forEach((e) => window.addEventListener(e, start, { passive: true }));
    start(); // when audio is already unlocked (e.g. unmuting mid-game)
    return () => events.forEach((e) => window.removeEventListener(e, start));
  }, [muted, getCtx, startPad, startTrack, stopTrack, stopPad]);

  useEffect(() => () => { stopPad(); stopTrack(); }, [stopPad, stopTrack]);

  /** @param {number} intensity 0–4, raises the pitch as the orb charges */
  const playTap = useCallback((intensity = 0) => {
    const ctx = getCtx();
    if (!ctx) return;
    const now = ctx.currentTime;
    tone(ctx, { freq: 220 + intensity * 55 + Math.random() * 30, start: now, duration: 0.18, peak: 0.12 });
    tone(ctx, { freq: 440 + intensity * 90, start: now + 0.01, duration: 0.12, peak: 0.05, type: 'triangle' });
  }, [getCtx, tone]);

  const playReveal = useCallback(() => {
    const ctx = getCtx();
    if (!ctx) return;
    const now = ctx.currentTime;
    [392, 523.25, 659.25, 783.99].forEach((freq, i) => {
      tone(ctx, { freq, start: now + i * 0.08, duration: 1.1, peak: 0.1, type: 'sine' });
    });
  }, [getCtx, tone]);

  const toggleMute = useCallback(() => setMuted((m) => !m), []);

  return { muted, toggleMute, playTap, playReveal };
}
